import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { AlertTriangle, Check, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAnalytics } from 'hooks/use-analytics'
import { IntegrationsService, OpenAPI, type IntegrationResponseDto } from 'openapi'
import { oauthUtils } from 'modules/oauth/utils/oauth-utils'
import { finishOAuthPickerStep } from '../utils/oauth-popup-complete'

export function InstagramAccountConfirmPage() {
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const [params] = useSearchParams()
  const businessId = params.get('businessId') ?? undefined
  const fromOAuth = params.get('fromOAuth') === '1'

  const [integration, setIntegration] = useState<IntegrationResponseDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [switching, setSwitching] = useState(false)

  useEffect(() => {
    let cancelled = false
    IntegrationsService.integrationControllerGetIntegrationStatusV1('instagram')
      .then(res => {
        if (cancelled) return
        setIntegration(res)
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load Instagram integration status:', err)
        setError('We could not load your Instagram connection. Please try again.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handle = typeof integration?.config?.handle === 'string' ? integration.config.handle : null
  const providerAccountId =
    typeof integration?.config?.providerAccountId === 'string'
      ? integration.config.providerAccountId
      : undefined
  const igLogo = oauthUtils.getProviderLogo('instagram')

  const handleConfirm = useCallback(async () => {
    setConfirming(true)
    setError(null)
    try {
      const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : undefined
      const rawBase = import.meta.env.VITE_API_URL
      const apiBaseUrl = typeof rawBase === 'string' ? rawBase : 'http://localhost:8000'
      const res = await fetch(`${apiBaseUrl}/v1/auth/instagram/confirm`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ businessId }),
      })

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
      }

      finishOAuthPickerStep({ provider: 'instagram', fromOAuth, navigate })
    } catch (err) {
      console.error('Instagram confirm failed:', err)
      setError('We could not finalize the connection. Please try again.')
      setConfirming(false)
    }
  }, [businessId, fromOAuth, navigate])

  const handleSwitch = useCallback(() => {
    setSwitching(true)
    setError(null)
    track(AnalyticsEvents.Social.SOCIAL_OAUTH_ACCOUNT_SWITCH_REQUESTED, {
      platform: 'instagram',
      previous_provider_user_id: providerAccountId,
      business_id: businessId,
    })

    const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : ''
    const rawBase = import.meta.env.VITE_API_URL
    const apiBaseUrl = typeof rawBase === 'string' ? rawBase : 'http://localhost:8000'
    let url = `${apiBaseUrl}/v1/auth/instagram?token=${encodeURIComponent(token)}&source=integrations_page&forceReauth=1`
    if (businessId) {
      url += `&businessId=${encodeURIComponent(businessId)}`
    }
    window.location.href = url
  }, [businessId, providerAccountId, track])

  if (loading) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center bg-bg">
        <Loader2 className="h-6 w-6 animate-spin text-paper-faint" />
      </div>
    )
  }

  // Honest empty state: this page only makes sense mid-OAuth. Landing here
  // stateless (bookmark, typed URL, expired flow) used to render a generic
  // "Instagram account / Connected to Taka ✓" card for a connection that
  // doesn't exist (monkey HONESTY finding). Say so and route to Integrations.
  if (!integration?.isConnected) {
    return (
      <div className="mx-auto flex min-h-[500px] max-w-md flex-col items-center bg-bg px-6 py-10">
        <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-line bg-bg-2">
          <img src={igLogo} alt="Instagram" className="h-12 w-12 object-contain" />
        </div>
        <h1 className="mt-5 text-center text-xl font-semibold text-paper">
          No Instagram connection in progress
        </h1>
        <p className="mt-2 text-center text-sm text-paper-dim">
          This page confirms an Instagram connection right after you approve it. Start from
          Integrations to connect an account.
        </p>
        <button
          onClick={() => navigate('/integrations')}
          className="text-ink-on-acid mt-6 rounded-[9px] bg-acid px-4 py-2 text-[13.5px] font-semibold transition-all hover:-translate-y-px"
        >
          Go to Integrations
        </button>
      </div>
    )
  }

  return (
    <div className="mx-auto flex min-h-[500px] max-w-md flex-col items-center bg-bg px-6 py-10">
      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-line bg-bg-2">
        <img src={igLogo} alt="Instagram" className="h-12 w-12 object-contain" />
      </div>

      <h1 className="mt-5 text-center text-xl font-semibold text-paper">
        Is this the right Instagram account?
      </h1>
      <p className="mt-2 text-center text-sm text-paper-dim">
        Instagram silently picks whichever account you’re signed into in your browser. Make sure
        this is the one you meant to connect.
      </p>

      <div className="mt-6 flex w-full items-center gap-3 rounded-xl border border-line bg-bg-2 p-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-line bg-bg-3">
          <img src={igLogo} alt="" className="h-7 w-7 object-contain" />
        </div>
        <div className="min-w-0 flex-1">
          {handle ? (
            <p className="truncate font-medium text-paper">@{handle}</p>
          ) : (
            <p className="font-medium text-paper">Instagram account</p>
          )}
          <p className="text-xs text-paper-dim">Connected to Taka</p>
        </div>
        <Check size={18} className="text-success flex-shrink-0" />
      </div>

      {error && (
        <div className="alert mt-4 w-full" data-tone="error" role="alert">
          <AlertTriangle size={14} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <div className="mt-6 flex w-full flex-col gap-2">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={confirming || switching}
          className="text-ink-on-acid inline-flex h-11 w-full items-center justify-center gap-2 rounded-[10px] bg-acid text-sm font-medium transition-colors hover:bg-acid/90 disabled:opacity-60"
        >
          {confirming ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Connecting…
            </>
          ) : (
            'Yes, this is the right account'
          )}
        </button>
        <button
          type="button"
          onClick={handleSwitch}
          disabled={confirming || switching}
          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-[10px] border border-line-bright text-sm font-medium text-paper transition-colors hover:bg-bg-3 disabled:opacity-60"
        >
          {switching ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Switching…
            </>
          ) : (
            'Switch to a different account'
          )}
        </button>
      </div>
    </div>
  )
}
